import { Phone, Mail, Globe, MapPin, MessageCircle, Smartphone } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, CONTACT, OFFICES } from "@/data/content";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { scrollToHash } from "@/components/Navbar";

export const Footer = () => (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-omsivo-void">
        <div className="mx-auto max-w-[1440px] px-4 py-16 sm:px-6 lg:px-10 lg:py-20">
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <div className="flex items-center gap-4">
                        <Logo className="h-14 w-14" />
                        <span className="font-display text-2xl font-black tracking-[0.16em] text-white">OMSIVO</span>
                    </div>
                    <p className="mt-6 font-display text-sm font-bold uppercase leading-relaxed tracking-[0.12em] text-white/80">
                        Not just houses.<br />We build concepts.
                    </p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-flame">
                        From your first idea to your final key.
                    </p>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">
                        From homes to mega projects.
                    </p>
                    <div className="mt-7 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5" data-testid="footer-app-strip">
                        <Smartphone className="h-5 w-5 shrink-0 text-flame" />
                        <div>
                            <p className="font-display text-xs font-bold uppercase tracking-wider text-white">Download OMSIVO App</p>
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-flame">Coming Soon</p>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.26em] text-white/40">Quick Links</p>
                    <nav className="mt-5 flex flex-col gap-3" aria-label="Footer">
                        {NAV_LINKS.map((l) => {
                            const tid = `footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
                            const label = l.label.charAt(0) + l.label.slice(1).toLowerCase();
                            return l.to ? (
                                <Link key={l.label} to={l.to} data-testid={tid}
                                    className="text-sm text-white/65 transition-colors hover:text-flame">
                                    {label}
                                </Link>
                            ) : (
                                <a key={l.hash} href={l.hash}
                                    onClick={(e) => { e.preventDefault(); scrollToHash(l.hash); }}
                                    data-testid={tid}
                                    className="text-sm text-white/65 transition-colors hover:text-flame">
                                    {label}
                                </a>
                            );
                        })}
                    </nav>
                </div>

                <div className="lg:col-span-3">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.26em] text-white/40">Contact</p>
                    <div className="mt-5 space-y-3.5">
                        <a href={CONTACT.phoneTel} onClick={() => trackEvent(EVENTS.phoneClick, { location: "footer" })}
                            data-testid="footer-phone-link" className="flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-flame">
                            <Phone className="h-4 w-4 shrink-0 text-flame" /> {CONTACT.phoneDisplay}
                        </a>
                        <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link"
                            className="flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-flame">
                            <Mail className="h-4 w-4 shrink-0 text-flame" /> {CONTACT.email}
                        </a>
                        <span className="flex items-center gap-3 text-sm text-white/75">
                            <Globe className="h-4 w-4 shrink-0 text-flame" /> {CONTACT.website}
                        </span>
                        <a href={CONTACT.mapsFooter} target="_blank" rel="noopener noreferrer"
                            onClick={() => trackEvent(EVENTS.directionsClick, { location: "footer" })}
                            data-testid="footer-directions-link" className="flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-flame">
                            <MapPin className="h-4 w-4 shrink-0 text-flame" /> Get Directions
                        </a>
                        <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
                            onClick={() => trackEvent(EVENTS.whatsappClick, { location: "footer" })}
                            data-testid="footer-whatsapp-link" className="flex items-center gap-3 text-sm text-white/75 transition-colors hover:text-flame">
                            <MessageCircle className="h-4 w-4 shrink-0 text-flame" /> Chat on WhatsApp
                        </a>
                    </div>
                </div>

                <div className="lg:col-span-3">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.26em] text-white/40">Offices</p>
                    <div className="mt-5 space-y-5">
                        {OFFICES.map((o, i) => (
                            <div key={o.label} data-testid={`footer-office-${i}`}>
                                <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-flame">{o.label}</p>
                                {o.lines.map((l, li) => (
                                    <p key={li} className={li === 0 ? "mt-1.5 font-display text-sm font-bold text-white" : "text-xs leading-relaxed text-white/55"}>
                                        {l}
                                    </p>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/8 pt-8 sm:flex-row">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
                    © {new Date().getFullYear()} OMSIVO. All rights reserved.
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35" data-testid="footer-domains">
                    www.omsivo.com · www.omsivo.in · www.omsivo.org
                </p>
            </div>
        </div>
    </footer>
);
